import React, { useRef, useEffect, useLayoutEffect, useState } from 'react';
import ReactPlayer from 'react-player';
import { useSelector } from 'react-redux';
import { getNavbarHeight, getViewportDimensions } from '../../../../models/Utils';

import { ADALandmarks, ADAARIARoles } from '../../../../models/ADA';

import Story from '../Story/Story';
import StoryDisplay from '../StoryDisplay/StoryDisplay';

import './StoriesSection.scss';

const StoriesSection = React.forwardRef(({ general, sections, storyDisplayCallback }, theRef) => {
  const menuOpened = useSelector(state => state.data.menuOpened);
  const bigRef = useRef(null);
  const sectionRefs = sections.map((section) => useRef(null));
  const progressRef = useRef(null);
  const [expansion, setExpansion] = useState(sections.map((section) => false));
  const refExpansion = useRef(expansion);
  const refDimensions = useRef({
    midHeight: 0,
    viewportHeight: 0,
    navbarHeight: 0
  });
  const [availableHeight, setAvailableHeight] = useState(0);
  const [storyInDisplay, setStoryInDisplay] = useState(null);
  const focusHelper = useRef({
    section: 0,
    story: 0
  });

  const handleResize = () => {
    const viewport = getViewportDimensions();
    const navbarHeight = getNavbarHeight();
    refDimensions.current = {
      midHeight: (viewport.height + navbarHeight) / 2,
      viewportHeight: viewport.height,
      navbarHeight
    };
    setAvailableHeight(viewport.height - navbarHeight);
  };

  const handleScroll = () => {
    const newExpansion = [];
    for (let i = 0; i < sectionRefs.length; i++) {
      const sectionRect = sectionRefs[i].current.getBoundingClientRect();
      newExpansion[i] = sectionRect.top <= refDimensions.current.midHeight && sectionRect.top + sectionRect.height > refDimensions.current.midHeight;
    }
    if (!newExpansion.every((exp, i) => exp === refExpansion.current[i])) {
      setExpansion(newExpansion);
      refExpansion.current = newExpansion;
    }

    const bigRect = bigRef.current.getBoundingClientRect();
    const smallRect = progressRef.current.getBoundingClientRect();
    const progressTop = Math.max(0, Math.min(refDimensions.current.navbarHeight - bigRect.top, bigRect.height - smallRect.height))
    progressRef.current.style.top = `${progressTop}px`;
  };

  const rememberFocus = (sectionIndex, storyIndex) => {
    focusHelper.current = {
      section: sectionIndex,
      story: storyIndex
    };
  };

  const restoreFocus = () => {
    setTimeout(() => {
      sectionRefs[focusHelper.current.section].current
      .getElementsByClassName('credo-ss-story')[focusHelper.current.story] // Must be the same class as in Story.js
      .getElementsByClassName('credo-button')[0]
      .focus();
    }, 10);
  };

  const generateSection = (section, index) => {
    return (
      <div className="credo-ss-section" key={index} ref={sectionRefs[index]} role={ADAARIARoles.Group} aria-label={`Story group ${index+1}: ${section.IntroText}`}>
        <div className="credo-ss-section-label">{general.SectionLabel}</div>
        <div className="credo-ss-section-header" role={ADAARIARoles.Heading}>{section.IntroText}</div>
        <div className={`credo-ss-stories-container ${expansion[index] ? 'expanded' : 'collapsed'}`}>
          {section.Stories.map((story, i) => (
            <div className="credo-ss-story-wrapper" key={i}>
              {generateSeparator()}
              <Story
                story={story}
                index={i}
                general={general}
                onDisplayStory={() => {
                  setStoryInDisplay(story);
                  storyDisplayCallback(true);
                  rememberFocus(index, i);
                }}
                onFocusButton={handleScroll}
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const generateSeparator = () => {
    return (
      <div className="credo-ss-separator">
        <div className="credo-ss-separator-dot"></div>
        <div className="credo-ss-separator-dot"></div>
        <div className="credo-ss-separator-dot"></div>
        <div className="credo-ss-separator-dot"></div>
        <div className="credo-ss-separator-dot"></div>
      </div>
    );
  };

  useLayoutEffect(() => {
    handleResize();
    handleScroll();

    window.addEventListener('resize', handleResize);
    document.body.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('resize', handleResize);
      document.body.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div
      className="credo-stories-section"
      ref={bigRef}
      //role={ADAARIARoles.Feed}
      tabIndex="-1"
      aria-label="List of Stories"
    >
      <div 
        className="credo-ss-progress-container"
        ref={progressRef}
        style={{
          visibility: storyInDisplay ? 'hidden' : null
        }}
      >
        <div className="cpvi-grid-container">
          <div className="cpvi-grid-row">
            <div className="cpvi-grid-col w12">
              <div className="credo-ss-progress" style={{ height: `${availableHeight}px` }}>
                <div className="credo-ss-progress-dots">
                  {sections.map((section, i) => <div className={`credo-ss-progress-dot ${expansion[i] ? 'active' : ''}`} key={i}></div>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        className="cpvi-grid-container"
        style={{
          visibility: storyInDisplay ? 'hidden' : null
        }}
      >
        <div className="cpvi-grid-row">
          <div className="cpvi-grid-col w12">
            {sections.map((section, i) => generateSection(section, i))}
          </div>
        </div>
      </div>
      {
        storyInDisplay
        &&
        <StoryDisplay
          story={storyInDisplay}
          general={general}
          onClose={() => {
            setStoryInDisplay(null);
            storyDisplayCallback(false);
            restoreFocus();
          }}
        />
      }
    </div>
  );
});

export default StoriesSection;